<template>
  <a-modal
    :visible="visible"
    title="导出配置"
    width="960px"
    :footer="null"
    :body-style="{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }"
    @cancel="handleClose"
  >
    <p class="modal-description">
      导出所有已配置站点的线上版本配置（不含未发布草稿），客户端／下游可按版本号做缓存与增量更新
    </p>

    <a-table
      row-key="siteId"
      :columns="columns"
      :data-source="records"
      :pagination="false"
      size="middle"
      :scroll="{ x: 760, y: 260 }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'siteId'">
          {{ getSiteName(record.siteId) }}
        </template>
        <template v-else-if="column.dataIndex === 'versionName'">
          {{ record.versionName || '-' }}
        </template>
        <template v-else-if="column.dataIndex === 'items'">
          {{ Array.isArray(record.items) ? record.items.length : 0 }}
        </template>
        <template v-else-if="column.dataIndex === 'publishTime'">
          {{ record.publishTime || '-' }}
        </template>
        <template v-else-if="column.dataIndex === 'defaultMaterialNo'">
          {{ getDefaultMaterialNo(record) }}
        </template>
      </template>
    </a-table>

    <section class="json-section">
      <h3>数据预览（JSON）</h3>
      <pre>{{ jsonText }}</pre>
    </section>

    <div class="modal-footer">
      <a-button @click="handleClose">关闭</a-button>
      <a-button @click="handleCopy">复制 JSON</a-button>
      <a-button
        type="primary"
        :loading="downloadLoading"
        @click="handleDownload"
      >
        下载 JSON 文件
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import { computed, ref } from 'vue'
import { message } from 'ant-design-vue'
import { appTab_config_exportAllPublished_get } from '@/server/modules/25824.js'

export default {
  name: 'ExportConfigModal',
  props: {
    visible: Boolean,
    records: {
      type: Array,
      default: () => []
    },
    siteOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:visible'],
  setup(props, { emit }) {
    const columns = [
      { title: '站点', dataIndex: 'siteId', width: 170 },
      { title: '线上版本', dataIndex: 'versionName', width: 130 },
      { title: 'Tab 数', dataIndex: 'items', width: 100 },
      { title: '最近发布', dataIndex: 'publishTime', width: 200 },
      { title: '默认选中', dataIndex: 'defaultMaterialNo', width: 160 }
    ]
    const jsonText = computed(() => JSON.stringify(props.records, null, 2))
    const downloadLoading = ref(false)

    const getSiteName = siteId =>
      props.siteOptions.find(option => Number(option.value) === Number(siteId))
        ?.label || `站点 ${siteId}`
    const getDefaultMaterialNo = record =>
      record.items?.find(item => Number(item.isDefault) === 1)?.materialNo ||
      '-'
    const handleClose = () => emit('update:visible', false)
    const copyWithFallback = text => {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!copied) throw new Error('复制失败')
    }
    const handleCopy = async () => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(jsonText.value)
        } else {
          copyWithFallback(jsonText.value)
        }
        message.success('JSON 已复制')
      } catch (error) {
        message.error(error?.message || 'JSON 复制失败')
      }
    }
    const handleDownload = async () => {
      if (downloadLoading.value) return

      downloadLoading.value = true
      try {
        const response = await appTab_config_exportAllPublished_get()
        const blob =
          response instanceof Blob
            ? response
            : new Blob([response], { type: 'application/json;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `app-tab-config-${Date.now()}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        message.success('JSON 文件已下载')
      } catch (error) {
        message.error(error?.message || 'JSON 文件下载失败')
      } finally {
        downloadLoading.value = false
      }
    }

    return {
      columns,
      downloadLoading,
      getDefaultMaterialNo,
      getSiteName,
      handleClose,
      handleCopy,
      handleDownload,
      jsonText
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.ant-modal-title) {
  font-size: 22px;
  font-weight: 600;
}

:deep(.ant-table-thead > tr > th) {
  color: #595959;
  font-weight: 600;
}

.modal-description {
  margin: -4px 0 20px;
  color: #8c8c8c;
  line-height: 1.7;
}

.json-section {
  margin-top: 22px;

  h3 {
    margin-bottom: 10px;
    color: #595959;
    font-size: 15px;
  }

  pre {
    max-height: 280px;
    margin: 0;
    overflow: auto;
    padding: 16px;
    border: 1px solid #e8e1d5;
    border-radius: 8px;
    background: #faf7f0;
    color: #595959;
    font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-all;
  }
}

.modal-footer {
  display: flex;
  margin-top: 24px;
  justify-content: flex-end;
  gap: 12px;

  .ant-btn {
    min-width: 96px;
    height: 38px;
  }
}
</style>
